import React from 'react';
import { calculatePrayerTimePercentage, getPrayerAbbreviation } from '../../utils/prayerTimeUtils';

/**
 * Prayer time markers displayed on the sun progress bar
 *
 * Day bar (sunrise→sunset):   Dhuhr, Asr, Maghrib (Maghrib is at sunset = end of day)
 * Night bar (sunset→sunrise): Isha (start of night), Fajr (near end of night)
 */
const PrayerMarkers = ({ prayerTimes, times, timezone }) => {
  // Skip if required data is missing
  if (!prayerTimes || !times || (times.isDay !== true && times.isDay !== false)) {
    return null;
  }

  // Maghrib (sunset prayer) belongs on the DAY bar
  // Isha & Fajr belong on the NIGHT bar
  const dayPrayers = ['dhuhr', 'asr', 'maghrib'];
  const nightPrayers = ['isha', 'fajr'];

  // Pick which prayers to show based on current mode
  const prayersToShow = times.isDay ? dayPrayers : nightPrayers;

  return (
    <>
      {prayersToShow.map(prayerKey => {
        // Skip if prayer time doesn't exist
        if (!prayerTimes[prayerKey]) return null;

        // Calculate position percentage
        const percentage = calculatePrayerTimePercentage(
          prayerTimes[prayerKey],
          times.sunrise,
          times.sunset,
          times.isDay,
          prayerKey,
          timezone
        );

        // Skip if calculation failed or out of range
        if (percentage === null || percentage < 0 || percentage > 1) return null;

        // Determine display style based on day/night
        const bgColor = times.isDay ? "bg-yellow-400" : "bg-blue-400";
        const shadowColor = times.isDay
          ? "0 0 4px rgba(255, 200, 0, 0.6)"
          : "0 0 4px rgba(100, 150, 255, 0.6)";
        const tooltipBg = times.isDay ? "bg-yellow-400/90" : "bg-blue-400/90";
        const prayerName = prayerKey.charAt(0).toUpperCase() + prayerKey.slice(1);

        return (
          <div
            key={prayerKey}
            className={`absolute top-0 w-0.5 h-full ${times.isDay ? 'bg-yellow-400' : 'bg-blue-400'} z-10 group`}
            style={{
              left: `${percentage * 100}%`,
              boxShadow: shadowColor
            }}
          >
            {/* Prayer label with letter inside marker */}
            <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full ${bgColor} flex items-center justify-center text-black text-xs font-bold`}>
              {getPrayerAbbreviation(prayerKey)}
            </div>

            {/* Enhanced tooltip that appears on hover */}
            <div className={`absolute -top-10 left-1/2 -translate-x-1/2 px-2 py-1 ${tooltipBg} text-black text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20`}>
              <div className="font-semibold">{prayerName}</div>
              <div>{prayerTimes[prayerKey]}</div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default PrayerMarkers;
